import { CALL_API, Schemas } from '../middleware/api'
import get from 'lodash/get'
import queryString from 'query-string'
import { browserHistory } from 'react-router'
import { camelizeKeys } from 'humps'
import { normalize } from 'normalizr'

const DEFAULT_PAGE_SIZE = 50

export const RESET_ERROR_MESSAGE = 'RESET_ERROR_MESSAGE'

export function getHeaders(state) {
  const ret = { 
    'Content-Type': 'application/json',
    'Accept':'application/json',
  }
  if(state.authentication.isAuthenticated) {
    ret.Authorization = `Token ${state.authentication.token}`
  }

  return ret
}

// Resets the currently visible error message.
export const resetErrorMessage = () => ({
  type: RESET_ERROR_MESSAGE
})

const createEndpoint = ({baseEndpoint, state, queryParams, pageIndex, pageSize}) => {
  const pagedQuery = {...queryParams, offset: pageIndex*pageSize, limit: pageSize}
  return `${baseEndpoint}/?${queryString.stringify(pagedQuery)}`
}

export const pathNameKeygen = ({state, ownProps}) => {
  const location = state.routing.locationBeforeTransitions
  return location ? location.pathname + location.search : 'pathNameKeygen-unknown-location'
}

export const ITEMS_REQUEST = 'ITEMS_REQUEST'
export const ITEMS_SUCCESS = 'ITEMS_SUCCESS'
export const ITEMS_FAILURE = 'ITEMS_FAILURE'

export const ITEM_REQUEST = 'ITEM_REQUEST'
export const ITEM_SUCCESS = 'ITEM_SUCCESS'
export const ITEM_FAILURE = 'ITEM_FAILURE'

export function bindKeywordArguments(fn, args) {
  return function(nextArgs) {    
    return fn({...nextArgs, ...args})
  }
}

function getPageFromPagination(pagination, index) {
  let { pages, pageSize } = pagination

  const pageIndex = Math.floor(index / pageSize)
  const page = (pages || {})[pageIndex]

  return { page, pageIndex }
}

export function getItemFromPagination(pagination, entities, index) {
  if (!pagination || !pagination.pageSize) { return }
  const pageIndex = Math.floor(index / pagination.pageSize)
  const itemIndex = index % pagination.pageSize
  const page = pagination.pages[pageIndex]

  if (page && page.ids.length > itemIndex) {
    const ret = entities[page.ids[itemIndex]]
    return pagination ? {...ret, isStale: true} : ret
  }
}

const fetchItems = ({schema, endpoint, meta, token}) => ({
  [CALL_API]: {
    types: [ ITEMS_REQUEST, ITEMS_SUCCESS, ITEMS_FAILURE ],
    endpoint,
    schema: [ schema ], // We are fetching a list so we wrap schema in an array
    meta,
    token,
  }
})

export const loadItems = ({
  schema, 
  baseEndpoint,
  keygen, 
  queryParams, 
  index, 
  pageSize,
  ownProps,
}) => (dispatch, getState) => {
  const key = keygen({state: getState(), ownProps})   
  const state = getState()
  pageSize = pageSize || DEFAULT_PAGE_SIZE
  const pagination = get(state, ['pagination', schema.key, key], { pageSize })

  const { page, pageIndex } = getPageFromPagination(pagination, index)
  if (page && page.isFetching) { return null }
  if (page && page.ids.length > 0 && !page.hasStaleData) { return null }

  const endpoint = createEndpoint({baseEndpoint, queryParams, pageIndex, pageSize})
  return dispatch(fetchItems({
    key, 
    endpoint, 
    schema, 
    token: getState().authentication.token,
    meta: {
      pageIndex, 
      pageSize, 
      key, 
      queryParams, 
      endpoint, 
      location: state.routing.locationBeforeTransitions,
    }
  }))
}

export const loadItem = ({
  schema,
  baseEndpoint,
  queryParams,
  ownProps,
  id,
}) => (dispatch, getState) => {
  return dispatch({
    [CALL_API]: {
      types: [ITEM_REQUEST, ITEM_SUCCESS, ITEM_FAILURE],
      endpoint: `${baseEndpoint}?${queryString.stringify(queryParams || {})}`,
      token: getState().authentication.token,
      schema: schema,
      meta: {
        endpoint: baseEndpoint, 
        location: getState().routing.locationBeforeTransitions,
        id: id,
      }
    }
  })
}

export const loadPlayers = bindKeywordArguments(loadItems, {
  schema: Schemas.PLAYER, 
  baseEndpoint: '/players',
  keygen: pathNameKeygen,
})

export const loadBets = bindKeywordArguments(loadItems, {
  schema: Schemas.BET, 
  baseEndpoint: '/bets',
  keygen: pathNameKeygen,
})

export const loadTickets = bindKeywordArguments(loadItems, {
  schema: Schemas.TICKET, 
  baseEndpoint: '/tickets',
  keygen: pathNameKeygen,
})

export const loadBetRecommendations = bindKeywordArguments(loadItems, {
  schema: Schemas.BET_RECOMMENDATION, 
  baseEndpoint: '/bet_recommendations',
  pageSize: 25
})

export const loadCouponRecommendations = bindKeywordArguments(loadItems, {
  baseEndpoint: `/coupon_recommendations`,
  schema: Schemas.BET_RECOMMENDATION,  
  keygen: pathNameKeygen,
  pageSize: 25
})

export const loadTicketRecommendations = (ticketId) => {
  return bindKeywordArguments(loadItems, {
    baseEndpoint: `/tickets/${ticketId}/recommendations`,
    schema: Schemas.BET_RECOMMENDATION,  
    keygen: pathNameKeygen,
    pageSize: 25
  })
}

export const loadProjects = bindKeywordArguments(loadItems, {
  schema: Schemas.PROJECT, 
  baseEndpoint: '/projects',
  keygen: pathNameKeygen,
})

export const loadRecommenderRules = bindKeywordArguments(loadItems, {
  schema: Schemas.RECOMMENDER_RULE,
  baseEndpoint: '/recommender_rules',
  keygen: pathNameKeygen,
  pageSize: 10000
})

export const CREATE_ITEM_REQUEST = 'CREATE_ITEM_REQUEST'
export const CREATE_ITEM_SUCCESS = 'CREATE_ITEM_SUCCESS'
export const CREATE_ITEM_FAILURE = 'CREATE_ITEM_FAILURE'

export const DELETE_ITEM_REQUEST = 'DELETE_ITEM_REQUEST'
export const DELETE_ITEM_SUCCESS = 'DELETE_ITEM_SUCCESS'
export const DELETE_ITEM_FAILURE = 'DELETE_ITEM_FAILURE'

export const UPDATE_ITEM_REQUEST = 'UPDATE_ITEM_REQUEST'
export const UPDATE_ITEM_SUCCESS = 'UPDATE_ITEM_SUCCESS'
export const UPDATE_ITEM_FAILURE = 'UPDATE_ITEM_FAILURE'

export const createItem = ({schema, endpoint, data}) => (dispatch, getState) => {
  dispatch({ type: CREATE_ITEM_REQUEST, schema, data })

  return fetch(endpoint, { 
    method: 'post',
    headers: getHeaders(getState()),
    body: JSON.stringify(data)
  })
    .then(response => {
        return response.json()
          .then(json => ({ json, response }))
          .catch(error => ({ error, response }))
    })
    .then( ({json, error, response}) => {
      const camelizedJson = camelizeKeys(json || {})
      const normalized = normalize(camelizedJson, schema)

      const action = response.ok ?
        { type: CREATE_ITEM_SUCCESS, schema, data, raw: json, response: normalized } :
        { type: CREATE_ITEM_FAILURE, schema, data, error }
      dispatch(action)

      return { json, error, response }
    })
}

export const deleteItem = ({schema, endpoint}) => (dispatch, getState) => {
  dispatch({ type: DELETE_ITEM_REQUEST, schema })

  return fetch(endpoint, {
    method: 'delete',
    headers: getHeaders(getState()),
  })
    .then(response => {
        return response.json()
          .then(json => ({ json, response }))
          .catch(error => ({ error, response }))
    })
    .then( ({error, response}) => {
      const action = response.ok ?
        { type: DELETE_ITEM_SUCCESS, schema, } :
        { type: DELETE_ITEM_FAILURE, schema, error }
      dispatch(action)

      return { error, response }
    })
}

export const saveArticle = bindKeywordArguments(createItem, {
  schema: Schemas.SAVED_ARTICLE,
  endpoint: '/api/saved_articles/',
})

export const likeArticle = (streamId, article) => (dispatch, getState) => {
  return createItem({
    schema: Schemas.SAVED_ARTICLE,
    endpoint: '/api/saved_articles/',
    data: {like: true, article: article.id, stream: streamId, is_manual: true}
  })(dispatch, getState)
}

export const dislikeArticle = (streamId, article) => (dispatch, getState) => {
  return createItem({
    schema: Schemas.SAVED_ARTICLE,
    endpoint: '/api/saved_articles/',
    data: { like: false, article: article.id, stream: streamId, is_manual: true }
  })(dispatch, getState)
}

export const LOGIN_REQUEST = 'LOGIN_REQUEST'
export const LOGIN_SUCCESS = 'LOGIN_SUCCESS'
export const LOGIN_ERROR = 'LOGIN_ERROR'
export const request = ({endpoint, baseType, method, onSuccess, meta, ...data}) => (dispatch, getState) => {
  const type = baseType.toUpperCase()
  dispatch({ type: `${type}_REQUEST`, data })

  const requestData = { 
    method: method || 'post', 
    headers: getHeaders(getState())    
  }

  const normalizedMethodName = method.toLowerCase()
  if(normalizedMethodName === 'post' || normalizedMethodName === 'put') {
    requestData.body = JSON.stringify(data)
  }

  return fetch(endpoint, requestData)
    .then(response => {
        return response.json()
          .then(json => ({ json, response }))
          .catch(error => ({ error, response }))
    })
    .then( ({json, error, response}) => {
      const action = response.ok ?
        { type: `${type}_SUCCESS`, json, data, meta } :
        { type: `${type}_ERROR`, error, data, meta }
      dispatch(action)

      if(onSuccess) {
        onSuccess(getState(), data)
      }

      return { json, error, response }
    })
}

export const login = bindKeywordArguments(request, {
  endpoint: 'get_auth_token/', 
  baseType: 'LOGIN', 
  method: 'POST',
  onSuccess: function(state, data) {
    const location = state.routing.locationBeforeTransitions
    if(location.query.next) {
      browserHistory.push(location.query.next)
    }

    localStorage.setItem('authentication', JSON.stringify(state.authentication))
  }
})

export const ITEMS_REFRESH = 'ITEMS_REFRESH'
export const refreshItems = (key) => (dispatch, getState) => (
  dispatch({type: ITEMS_REFRESH, key})
)

export const RESTORE_STATE_KEY = 'RESTORE_STATE_KEY'
export const restoreStateKey = (key, value) => (dispatch, getState) => (
  dispatch({type: RESTORE_STATE_KEY, key, value})
)

export const REFRESH_TOKEN_REQUEST = 'REFRESH_TOKEN_REQUEST'
export const REFRESH_TOKEN_SUCCESS = 'REFRESH_TOKEN_SUCCESS'
export const REFRESH_TOKEN_ERROR = 'REFRESH_TOKEN_ERROR'
export const refreshToken = bindKeywordArguments(request, {
  endpoint: '/api-token-refresh/',
  baseType: 'REFRESH_TOKEN', 
  method: 'POST',
})

export const createStream = (name) => (dispatch, getState) => {
  return createItem({
    schema: Schemas.STREAM,
    endpoint: '/api/streams/',
    data: { name }
  })(dispatch, getState)
}

export const deleteStream = (stream) => (dispatch, getState) => {
  return request({
    endpoint: `/api/streams/${stream.id}/`, 
    baseType: 'DELETE_ITEM', 
    method: 'DELETE',
    meta: { schema: Schemas.STREAM },
  })(dispatch, getState)
}

export const loadPlayer = (playerId) => (dispatch, getState) => {
  return loadItem({
    baseEndpoint: `/players/${playerId}/`,
    schema: Schemas.PLAYER,
    id: playerId,    
  })(dispatch, getState)
}

export const loadProject = (modelId) => (dispatch, getState) => {
  return loadItem({
    baseEndpoint: `/projects/${modelId}/`,
    schema: Schemas.PROJECT,
    id: modelId,
    queryParams: {expand: 'active_tasks'},
  })(dispatch, getState)
}

export const updateProject = (project) => (dispatch, getState) => {
  return request({
    endpoint: `/api/projects/${project.modelId}/`,
    baseType: 'UPDATE_ITEM',
    method: 'PUT',
    meta: { schema: Schemas.PROJECT },
    data: {project: project.modelId, is_active: !project.isActive}
  })(dispatch, getState)
}

export const createProject = bindKeywordArguments(createItem, {
  schema: Schemas.PROJECT,
  endpoint: '/api/projects/',
  data: {
    model_path: "training", 
    transformer_path: "training",
    is_active: false
  }
})

// export const createProject = () => (dispatch, getState) => {
//   return request({
//     endpoint: `/api/projects/`,
//     baseType: 'CREATE_ITEM',
//     method: 'POST',
//     meta: { schema: Schemas.PROJECT },
//     model_path: "training", 
//     transformer_path: "training",
//     is_active: false
//   })(dispatch, getState)
// }

export const createProjectTask = (projectId, taskType) => (dispatch, getState) => {
  return request({
    endpoint: `/api/projects/${projectId}/tasks/`,
    baseType: 'CREATE_TASK',
    method: 'POST',
    meta: { schema: Schemas.PROJECT_TASK },
    task_type: taskType
  })(dispatch, getState)
}

export const deleteProjectTask = (projectId, taskId) => (dispatch, getState) => {
  return request({
    endpoint: `/api/projects/${projectId}/tasks/${taskId}/`,
    baseType: 'DELETE_TASK',
    method: 'DELETE',
    meta: { schema: Schemas.PROJECT_TASK },
  })(dispatch, getState)
}

export const addParamsToData = (params, args) => {
  let data = {}
  params.forEach(p => {
    let arg = get(args, p);
    if (arg) {
      data[p]= arg;
    }
  });
  return data;
}

export const createRecommenderRule = (args) => (dispatch, getState) => {
  const params = ['player_group_id', 'rule_item_type', 'rule_item_id', 'rule_factor', 'rule_type']
  let data = addParamsToData(params, args)
  return createItem({
    schema: Schemas.RECOMMENDER_RULE,
    endpoint: '/api/recommender_rules/',
    data: data
  })(dispatch, getState)
}


export const deleteRecommenderRule = (id) => (dispatch, getState) => {
  return deleteItem({
    endpoint: `/api/recommender_rules/${id}/`,
    schema: Schemas.RECOMMENDER_RULE,
  })(dispatch, getState)
}



// WEBPACK FOOTER //
// ./src/actions/index.js